import React, { useEffect } from "react";
import "../../styles/components/FormBuilder.scss";
import { ClinicalInfoInterface } from "../../types/PatientData.interface";
import RadioToggleButton from "./inputs/RadioToggleButton";
import CustomDropdown from "./inputs/CustomDropdown";
import CustomTextInput from "./inputs/CustomTextInput";
import CustomDatePicker from "./DatePicker/DatePicker";
import nestLine from "../../common/assats/icons/nestLine.svg";
import { useTranslation } from "react-i18next";
import CustomNumberInput from "./inputs/CustomNumberInput";
import CustomAutoComplete from "./inputs/CustomAutoComplete";
import CustomDropdownWithTextInput from "./inputs/CustomDropdownWithTextInput";
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'
import { MultiSelect } from "react-multi-select-component";

export interface FormBuilderInterface {
  name: string;
  label?: string;
  additionalLable?: string;
  type?: string;
  colWidth?: string;
  suffix?: string;
  translateOffset?: boolean;
  offset?: string;
  optionsArray?: string[];
  question?: string;
  required?: boolean;
  nested?: number;
  translateLabel?: boolean;
  translateOptions?: boolean;
  fullRow?: boolean;
  icon?: React.ReactNode;
  isDisabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  value?: string | number | Date | null;
  scrollableYearDropdown?: boolean;
  customInputTrigger?: string;
  checkMetric?: boolean;
  metric?: string;
}
export interface CustomDateChangeEvent {
  target: {
    name: string;
    value: Date | null;
  };
}

interface FormBuilderProps {
  fields: FormBuilderInterface[];
  handleInputChange: any;
  handleFocus?: any;
  handleBlur?: any;
  values: any;
  showErrors?: boolean;
  errors?: { [key: string]: string | null };
  translateErrors?: boolean;
  checkMetric?: string
}

const FormBuilder = (props: FormBuilderProps) => {
  const { t } = useTranslation();
  const {
    fields,
    handleInputChange,
    values,
    showErrors,
    errors,
    handleBlur,
    handleFocus,
    checkMetric
  } = props;
  const booleanDisplay = (value: boolean | undefined | string): string => {
    return typeof value === "boolean"
      ? value
        ? "Yes"
        : "No"
      : value
        ? value
        : "";
  };

  const translateOptions = (options: string[]) => {
    return options.map((option) => t(option));
  };

  const getFieldLabel = (field: FormBuilderInterface) => {
    const baseLabel = field.translateLabel ? t(field.label!) : field.label;
    return field.additionalLable ? `${baseLabel} (${field.additionalLable})` : baseLabel;
  };

  return (
    <div className="generic-form">
      <div className="row">
        {fields.map((field, i) => (
          !field.checkMetric || (field.checkMetric && field.metric === checkMetric) ?
            <div
              className={`${field.nested ? "has-nested" : ""}${field.offset ? ` offset-${field.offset}` : ""} ${field.fullRow ? "col-sm-12" : field.colWidth ?? "col-sm-4"} ${field.translateOffset ? "translate-offset" : ""} mb-3`}
              key={`${field.name}${i}`}
            >
              {field.nested && <img src={nestLine} className="nest-line" />}
              <div className={`form-group w-100`}>
                {field.type === "dropdown" ? (
                  <CustomDropdown
                    name={field.name}
                    label={getFieldLabel(field)}
                    optionsArray={
                      field.optionsArray
                        ? field.translateOptions
                          ? translateOptions(field.optionsArray!)
                          : field.optionsArray
                        : []
                    }
                    value={values[field.name]}
                    error={errors?.[field.name] || ""}
                    handleInputChange={handleInputChange}
                    handleBlur={handleBlur}
                    showErrors={showErrors}
                    required={field.required}
                    handleFocus={handleFocus}
                    disabled={field.isDisabled}
                  />
                ) : field.type === "multiSelect" ? (
                  <MultiSelect
                    className={"custom-multiselect"}
                    hasSelectAll={false}
                    isCreatable={true}
                    ClearSelectedIcon={null}
                    overrideStrings={{
                      "allItemsAreSelected": "All items are selected.",
                      "clearSearch": "Clear Search",
                      "clearSelected": "Clear Selected",
                      "noOptions": "No options",
                      "search": "Search or Other",
                      "selectAll": "Select All",
                      "selectAllFiltered": "Select All (Filtered)",
                      "selectSomeItems": `${getFieldLabel(field)} *`,
                      "create": "Create",
                    }}
                    valueRenderer={(selected, _options) => {
                      // Custom logic: show number of selected items or comma list
                      if (selected.length === 0) {
                        return <span style={{ color: 'black' }}>{getFieldLabel(field)} <span style={{ color: 'red' }}>*</span></span>;
                      }

                      // if (selected.length === _options.length) {
                      //   return <span>{selected.length} items selected</span>; // suppress "All items are selected"
                      // }

                      return <span>{selected.map(s => s.label).join(', ')}</span>;
                    }}
                    onMenuToggle={(isOpen: boolean) => {
                      if (!isOpen) {
                        console.log("Final selected:", values[field.name]);
                      }
                    }}
                    options={(
                      field.translateOptions
                        ? translateOptions(field.optionsArray ?? [])
                        : field.optionsArray ?? []
                    ).map((o) => ({ label: o, value: o }))}

                    // Распарсиваем JSON-строку обратно в массив объектов
                    value={
                      (() => {
                        try {
                          const raw = values[field.name];
                          const parsed: string[] = Array.isArray(raw)
                            ? raw
                            : typeof raw === "string"
                              ? JSON.parse(raw)
                              : [];

                          return parsed.map((v: string) => ({ label: v, value: v }));
                        } catch (e) {
                          return [];
                        }
                      })()
                    }

                    // Сохраняем как JSON-строку
                    onChange={(selected: { label: string; value: string }[]) =>
                      handleInputChange({
                        target: {
                          name: field.name,
                          value: JSON.stringify(selected.map((s) => s.value)),
                        },
                      })
                    }

                    labelledBy={getFieldLabel(field) || field.name}
                  />

                ) : field.type === "text" ? (
                  <CustomTextInput
                    name={field.name}
                    label={getFieldLabel(field)}
                    value={values[field.name]}
                    error={errors?.[field.name] || ""}
                    handleInputChange={handleInputChange}
                    showErrors={showErrors}
                    handleBlur={handleBlur}
                    suffix={field.suffix}
                    required={field.required}
                    handleFocus={handleFocus}
                    translateErrors={props.translateErrors}
                    icon={field.icon}
                    disabled={field.isDisabled}
                  />
                ) : field.type === "number" ? (
                  <CustomNumberInput
                    name={field.name}
                    label={getFieldLabel(field)}
                    value={
                      field.type === "number"
                        ? values[field.name]
                          ? values[field.name]
                          : ""
                        : values[field.name]
                    }
                    error={errors?.[field.name] || ""}
                    handleInputChange={handleInputChange}
                    showErrors={showErrors}
                    handleBlur={handleBlur}
                    required={field.required}
                    handleFocus={handleFocus}
                    translateErrors={props.translateErrors}
                    icon={field.icon}
                    disabled={field.isDisabled}
                  />
                ) : field.type === "date" ? (
                  <CustomDatePicker
                    name={field.name}
                    label={getFieldLabel(field)}
                    handleBlur={handleBlur}
                    value={values[field.name]}
                    error={errors?.[field.name] || ""}
                    handleInputChange={handleInputChange}
                    showErrors={showErrors}
                    handleFocus={handleFocus}
                    required={field.required}
                    disabled={field.isDisabled}
                    minDate={field.minDate}
                    maxDate={field.maxDate}
                  />
                ) : field.type === "radio" ? (
                  <div className={`toggle-question ${field.nested ? `nested-${field.nested}` : ""}`}
                    key={field.name}
                  >
                    <div className="question-text">
                      {field.translateLabel ? t(field.question!) : field.question} <span className="required-asterisk">{field?.required ? "*" : ""}</span>
                    </div>
                    <RadioToggleButton
                      name={field.name as keyof ClinicalInfoInterface}
                      optionsArray={
                        field.optionsArray
                          ? field.translateOptions
                            ? translateOptions(field.optionsArray!)
                            : field.optionsArray
                          : []
                      }
                      selectedOption={booleanDisplay(
                        values[field.name as keyof typeof values]
                      )}
                      handleInputChange={handleInputChange}
                      disabled={field.isDisabled}
                    />
                  </div>
                ) : field.type === "info" ? (
                  <div className="info-field">
                    <label className="form-label">
                      {field.translateLabel ? t(field.label!) : field.label}
                    </label>
                    <span className="info-value">{values[field.name] ? values[field.name]?.toString() : ""}</span>
                  </div>
                ) : field.type === "autoComplete" ? (
                  <CustomAutoComplete
                    name={field.name}
                    label={getFieldLabel(field)}
                    optionsArray={
                      field.optionsArray
                        ? field.translateOptions
                          ? translateOptions(field.optionsArray!)
                          : field.optionsArray
                        : []
                    }
                    value={values[field.name]}
                    error={errors?.[field.name] || ""}
                    handleInputChange={handleInputChange}
                    handleBlur={handleBlur}
                    handleFocus={handleFocus}
                    showErrors={showErrors}
                    required={field.required}
                    translateErrors={props.translateErrors}
                    icon={field.icon}
                    disabled={field.isDisabled}
                  />
                ) : field.type === "dropdownWithText" ? (
                  <CustomDropdownWithTextInput
                    name={field.name}
                    label={getFieldLabel(field)}
                    optionsArray={field.optionsArray
                      ? field.translateOptions
                        ? translateOptions(field.optionsArray!)
                        : field.optionsArray
                      : []}
                    value={values[field.name]}
                    error={errors?.[field.name] || ""}
                    handleInputChange={handleInputChange}
                    handleBlur={handleBlur}
                    showErrors={showErrors}
                    required={field.required}
                    handleFocus={handleFocus}
                    disabled={field.isDisabled}
                    customInputTrigger={field.customInputTrigger || "Other"}
                  />
                ) : field.type === "phone" ?
                  <div>
                    <div className={`phone-input-wrapper ${showErrors && errors?.[field.name] ? 'is-invalid' : ''}`} style={{ position: 'relative' }}>
                      <PhoneInput
                        isValid={!errors?.[field.name]}
                        placeholder={' '}
                        value={values[field.name]}
                        onChange={(phone) =>
                          handleInputChange({ target: { name: field.name, value: phone } })
                        }
                        inputClass={`country-phone-input custom-input ${showErrors && errors?.[field.name] ? 'is-invalid' : ''}`}
                        containerClass={`country-phone-container ${showErrors && errors?.[field.name] ? 'is-invalid' : ''}`}
                        buttonClass="country-phone-flag"
                        specialLabel=""
                        onBlur={handleBlur}
                        onFocus={handleFocus}
                      />

                      {!values[field.name] && (
                        <div
                          style={{
                            position: 'absolute',
                            left: 50,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            pointerEvents: 'none',
                            color: '#757575',
                            fontSize: '16px',
                            fontFamily: 'Poppins-Regular, sans-serif',
                          }}
                        >
                          Phone Number <span style={{ color: 'red' }}>*</span>
                        </div>
                      )}

                    </div>
                    {showErrors && errors?.[field.name] && <div className="invalid-feedback">{props.translateErrors ? t(errors?.[field.name]!) : errors?.[field.name]}</div>}
                  </div>
                  : null
                }
              </div>
            </div>
            : null
        ))}
      </div>
    </div>
  );
};

export default FormBuilder;